<script>
  import { onMount } from "svelte";
  export let project;
  let showVideo = false;
  let videoId = "";

  // Determine if we're on a mobile device
  let isMobile = false;

  if (typeof window !== "undefined") {
    isMobile = window.innerWidth < 768;
    // Add resize listener
    const handleResize = () => {
      isMobile = window.innerWidth < 768;
    };
    window.addEventListener("resize", handleResize);
  }

  onMount(() => {
    if (project.demoVideo) {
      const url = new URL(project.demoVideo);
      videoId = url.searchParams.get("v") || project.demoVideo.split("/").pop();
    }
  });

  function toggleVideo() {
    showVideo = !showVideo;
  }
</script>

<div
  class="group relative backdrop-blur-xl bg-white/30 dark:bg-gray-800/20 border border-white/30 dark:border-gray-700/30
  rounded-3xl overflow-hidden flex flex-col h-full transition-all duration-500
  hover:scale-[1.02] hover:shadow-[0_20px_40px_-15px_rgba(59,130,246,0.15)] dark:hover:shadow-[0_20px_40px_rgba(59,130,246,0.15)]
  hover:border-blue-100/50 dark:hover:border-blue-500/30 backdrop-saturate-[1.8]"
>
  <!-- Media Section -->
  {#if project.photos?.length > 0 || project.demoVideo}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div
      class="relative aspect-[16/5] md:aspect-[16/5] overflow-hidden"
      class:cursor-pointer={project.demoVideo}
      on:click={toggleVideo}
    >
      {#if showVideo && videoId}
        <iframe
          title="Demo Video"
          class="absolute inset-0 w-full h-full"
          src="https://www.youtube.com/embed/{videoId}?autoplay=1"
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
        ></iframe>
      {:else}
        {#if project.photos?.length > 0}
          <div
            class="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent z-10"
          ></div>
          <img
            src={project.photos[0]}
            alt={project.name}
            loading="lazy"
            class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        {:else}
          <div
            class="w-full h-full bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20"
          />
        {/if}

        {#if project.demoVideo}
          <div class="absolute inset-0 flex items-center justify-center z-20">
            <div
              class="p-3 md:p-4 rounded-full bg-white/30 backdrop-blur-xl border border-white/30 shadow-[0_4px_30px_rgba(0,0,0,0.1)] transition-transform duration-500 group-hover:scale-110 hover:bg-white/40"
            >
              <svg
                class="w-6 h-6 md:w-8 md:h-8 text-white drop-shadow-lg"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        {/if}
      {/if}

      {#if project.competation}
        <div
          class="absolute top-2 md:top-4 right-2 md:right-4 flex items-center gap-1 md:gap-2 bg-white/30 backdrop-blur-xl border border-white/30 shadow-[0_4px_30px_rgba(0,0,0,0.1)] rounded-lg p-1 md:p-2 z-30"
        >
          <img
            src={project.competation_logo}
            alt={project.competation}
            class="h-4 w-4 md:h-5 md:w-5"
          />
          <span class="text-2xs md:text-xs text-white font-medium"
            >{project.competation}</span
          >
        </div>
      {/if}
    </div>
  {/if}

  <!-- Content -->
  <div class="flex flex-col flex-grow p-4 md:p-8">
    <h3
      class="text-lg md:text-xl font-bold text-gray-900 dark:text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 transition-colors duration-500 mb-1 md:mb-2"
    >
      {project.name}
    </h3>
    <p
      class="text-sm md:text-base text-gray-700 dark:text-gray-300 leading-relaxed mb-4 md:mb-6"
    >
      {project.description}
    </p>

    <!-- Technologies -->
    <div class="flex flex-wrap gap-1 md:gap-2 mb-4 md:mb-6">
      {#each project.technologies as tech}
        <span
          class="px-2 md:px-3 py-1 md:py-1.5 rounded-full text-xs md:text-sm font-medium
          bg-white/50 dark:bg-gray-900/50
          text-gray-700 dark:text-gray-300
          border border-gray-200/50 dark:border-gray-700/50
          hover:border-blue-500 dark:hover:border-blue-500
          backdrop-blur-sm
          transition-all duration-300 hover:scale-105"
        >
          {tech}
        </span>
      {/each}
    </div>

    {#if project.bounties && project.bounties.length > 0}
      <div
        class="mt-auto pt-2 md:pt-3 border-t border-gray-200/50 dark:border-gray-700/50"
      >
        <div class="flex flex-wrap gap-1 md:gap-1.5">
          {#each project.bounties as bounty}
            <span
              class="text-2xs md:text-xs text-blue-600 dark:text-blue-400 font-medium px-1.5 md:px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30"
            >
              {bounty}
            </span>
          {/each}
        </div>
      </div>
    {/if}
  </div>

  <!-- Footer -->
  <div
    class="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 p-4 md:p-8 border-t border-gray-200/50 dark:border-gray-700/50"
  >
    {#if project.competation_link}
      <a
        href={project.competation_link}
        target="_blank"
        rel="noopener noreferrer"
        class="text-xs md:text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
      >
        View Submission
      </a>
    {/if}
    <a
      href={project.github}
      target="_blank"
      rel="noopener noreferrer"
      class="md:ml-auto flex items-center gap-1 md:gap-2 text-xs md:text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
    >
      <svg
        class="w-3 h-3 md:w-4 md:h-4"
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"
        />
      </svg>
      View Source
    </a>
  </div>
</div>

<style>
  :global(.youtube-player) {
    aspect-ratio: 16/9;
    width: 100%;
  }

  /* Small text for mobile */
  .text-2xs {
    font-size: 0.65rem;
    line-height: 0.9rem;
  }

  /* Mobile optimization */
  @media (max-width: 768px) {
    /* Improve tap targets */
    a {
      min-height: 36px;
      display: flex;
      align-items: center;
    }
  }
</style>
